<template>
  <div id="quotes" class="p-4">
    <div class="row align-items-center align-item-middle pb-4">
      <div class="col-8">
        <h3 class="mb-0">Quotes</h3>
      </div>
      <div class="col-4 text-right">
        <button class="btn btn-primary btn-sm" @click="addQuote" v-if="canAddQuote">
          + Quote
        </button>
      </div>
    </div>

    <div v-loading="loading" class="quotes-container">
      <div v-if="!quotesData || !quotesData.quotes_by_venue" class="text-center py-4 text-muted">
        No Venue Quotes
      </div>

      <!-- Quote History Display -->
      <div v-else>
        <quote-history-display 
          :quotes-history="quotesData.quotes_by_venue" 
          :lead-event="leadEvent"
          :venues-info="venuesInfo"
          @edit-quote="editQuote"
          @quote-request-cancelled="onQuoteRequestCancelled"
        />
      </div>
    </div>
    <!-- Create/Update Quote Modal -->
    <CreateUpdateQuote
      :visible.sync="showQuoteModal"
      :lead-event="leadEvent"
      :available-venues="availableVenues"
      :quote-to-edit="selectedQuote"
      :quotes-data="quotesData"
      @quote-saved="onQuoteSaved"
      @quote-sent="onQuoteSent"
      @close="onModalClose"
    />
  </div>
</template>

<script>
import Badge from "@/components/Badge.vue";
import CreateUpdateQuote from "@/views/Widgets/LeadEventDetails/Quotes/CreateUpdateQuote.vue";
import QuoteHistoryDisplay from "@/views/Widgets/LeadEventDetails/Quotes/QuoteHistoryDisplay.vue";
import moment from "moment";
import { Message } from "element-ui";

export default {
  name: "LeadEventQuotes",
  components: {
    Badge,
    CreateUpdateQuote,
    QuoteHistoryDisplay,
  },
  props: {
    leadEvent: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      loading: false,
      showQuoteModal: false,
      selectedQuote: null,
    };
  },
  computed: {
    canAddQuote() {
      return this.availableVenues.length > 0;
    },
    quotesData() {
      // Use Vuex getter to get full quotes data reactively
      return this.$store.getters["leadEvent/getQuotesByLeadEventId"](this.leadEvent.id);
    },
    venuesInfo() {
      // Map of venueId -> venue object (with isQuoteRequested /
      // isQuoteRequestCancelled flags), sourced from enquiries and
      // recommendations. Consumed by <quote-history-display> to decide
      // whether the "Cancel Quote Request" action should render.
      const info = {};
      const enquiries = this.$store.getters["leadEvent/getEnquiriesByLeadEventId"](this.leadEvent.id) || [];
      const recommendations = this.$store.getters["recommendations/getRecommendationsByLeadEventId"](this.leadEvent.id) || [];
      enquiries.forEach(enquiry => {
        const v = enquiry?.venue;
        if (v && v.id != null) info[v.id] = v;
      });
      recommendations.forEach(rec => {
        // Recommendation entries may be a venue object directly or wrapped as { venue }.
        const v = rec?.venue || rec;
        if (v && v.id != null && !info[v.id]) info[v.id] = v;
      });
      return info;
    },
    availableVenues() {
      // Always get venues from enquiries and recommendations first
      const venues = [];
      const enquiries = this.$store.getters["leadEvent/getEnquiriesByLeadEventId"](this.leadEvent.id) || [];
      const recommendations = this.$store.getters["recommendations/getRecommendationsByLeadEventId"](this.leadEvent.id) || [];
      
      // Add venues from enquiries
      enquiries.forEach(enquiry => {
        if (enquiry.venue && !venues.find(v => v.id === enquiry.venue.id)) {
          venues.push({
            id: enquiry.venue.id,
            name: enquiry.venue.name
          });
        }
      });
      
      // Add venues from recommendations
      recommendations.forEach(rec => {
        if (rec.venue && !venues.find(v => v.id === rec.venue.id)) {
          venues.push({
            id: rec.venue.id,
            name: rec.venue.name
          });
        }
      });
      
      // Also add venues from quotesData (important for edit mode)
      if (this.quotesData && this.quotesData.quotes_by_venue) {
        // Handle new object structure where venue IDs are keys
        if (typeof this.quotesData.quotes_by_venue === 'object' && !Array.isArray(this.quotesData.quotes_by_venue)) {
          Object.keys(this.quotesData.quotes_by_venue).forEach(venueId => {
            const venueData = this.quotesData.quotes_by_venue[venueId];
            if (venueData && venueData.venue_id && venueData.venue_name) {
              // Only add if not already in the list
              if (!venues.find(v => v.id == venueData.venue_id)) { // Use == for loose comparison
                venues.push({
                  id: parseInt(venueData.venue_id), // Ensure consistent numeric type
                  name: venueData.venue_name
                });
              }
            }
          });
        } else if (Array.isArray(this.quotesData.quotes_by_venue)) {
          // Handle legacy array format
          this.quotesData.quotes_by_venue.forEach(quote => {
            if (quote.venue_id && quote.venue_name && !venues.find(v => v.id == quote.venue_id)) { // Use == for loose comparison
              venues.push({
                id: parseInt(quote.venue_id), // Ensure consistent numeric type
                name: quote.venue_name
              });
            }
          });
        }
      }
      
      return venues;
    }
  },
  async created() {
    await this.loadQuotes();
  },
  methods: {
    async loadQuotes() {
      this.loading = true;
      try {
        // Dispatch action to load quotes into Vuex store (stores full response structure)
        await this.$store.dispatch("leadEvent/getQuotes", this.leadEvent.id);
      } catch (error) {
        console.error("Error loading lead event quotes:", error);
        Message.error("Failed to load quotes");
      } finally {
        this.loading = false;
      }
    },

    getQuoteIcon(status) {
      const iconMap = {
        quote_requested_by_client: "fa fa-bell",
        quote_sent_v1_0: "fa fa-check",
        quote_change_request: "fa fa-minus",
        quote_sent_v1_1: "fa fa-check",
        client_approved: "fa fa-check",
        quote_draft: "fa fa-exclamation",
      };
      return iconMap[status] || "fa fa-file";
    },

    getQuoteColor(status) {
      const colorMap = {
        quote_requested_by_client: "#36a9e1",
        quote_sent_v1_0: "#28a745",
        quote_change_request: "#dc3545",
        quote_sent_v1_1: "#28a745",
        client_approved: "#28a745",
        quote_draft: "#ffc107",
      };
      return colorMap[status] || "#6c757d";
    },

    getQuoteStatusText(status) {
      const textMap = {
        quote_requested_by_client: "Quote requested by client",
        quote_sent_v1_0: "Quote sent (v1.0)",
        quote_change_request: "Quote change request",
        quote_sent_v1_1: "Quote sent (v1.1)",
        client_approved: "Client Approved",
        quote_draft: "Quote DRAFT (v1.2)",
      };
      return textMap[status] || status;
    },

    formatDateTime(dateString) {
      return moment(dateString).format("DD MMM YYYY");
    },

    formatTime(dateString) {
      return moment(dateString).format("h:mm a");
    },

    addQuote() {
      this.selectedQuote = null; // Clear any existing selection
      this.showQuoteModal = true;
    },

    editQuote(quote) {
      this.selectedQuote = quote;
      this.showQuoteModal = true;
    },

    async onQuoteRequestCancelled() {
      // Refresh quotes plus the venue lifecycle flags (isQuoteRequested /
      // isQuoteRequestCancelled) that drive the cancel button's visibility.
      await Promise.all([
        this.loadQuotes(),
        this.$store.dispatch("leadEvent/getEnquiries", this.leadEvent.id).catch(() => {}),
        this.$store.dispatch("recommendations/getRecommendations", this.leadEvent.id).catch(() => {}),
      ]);
    },

    onQuoteSaved() {
      this.loadQuotes(); // Reload quotes after save
    },

    onQuoteSent() {
      this.loadQuotes(); // Reload quotes after sending
    },

    onModalClose() {
      this.selectedQuote = null;
    }
  }
};
</script>

<style scoped>
.quotes-container {
  min-height: 200px;
}

.quote-timeline {
  position: relative;
  padding-left: 0;
}

.quote-item {
  margin-bottom: 2rem;
  position: relative;
}

.quote-item:last-child {
  margin-bottom: 0;
}

.quote-icon-container {
  position: relative;
  margin-right: 1rem;
  flex-shrink: 0;
}

.quote-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #f8f9fe;
  border: 2px solid #e3e6f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.quote-item:first-child .quote-icon {
  background-color: #5e72e4;
  border-color: #5e72e4;
  color: white;
}

.quote-content-container {
  background-color: #f8f9fe;
  border-radius: 0.5rem;
  padding: 1rem;
  border-left: 4px solid #5e72e4;
  min-width: 0; /* Allows content to shrink */
}

.quote-header h6 {
  color: #32325d;
  font-weight: 600;
}

.quote-time {
  font-size: 0.875rem;
  color: #8898aa;
  white-space: nowrap;
}

.quote-notes {
  background-color: #ffffff;
  border-radius: 0.375rem;
  padding: 0.75rem;
  border: 1px solid #e3e6f0;
  margin: 0.5rem 0;
}

.quote-attachment {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.attachment-item {
  flex-shrink: 0;
}

.quote-metadata {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.badge-success {
  background-color: #2dce89;
  color: white;
}

.badge-warning {
  background-color: #fb6340;
  color: white;
}

.quote-item-actions {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.quote-item:hover .quote-item-actions {
  opacity: 1;
}

/* Dialog styles */
.dialog-footer {
  text-align: right;
}

/* Timeline connector line */
.quote-timeline::before {
  content: '';
  position: absolute;
  left: 18px;
  top: 36px;
  bottom: 0;
  width: 2px;
  background-color: #e3e6f0;
  z-index: 0;
}

.quote-icon-container {
  position: relative;
  z-index: 1;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .quote-item {
    flex-direction: column;
  }
  
  .quote-icon-container {
    margin-bottom: 0.5rem;
    align-self: flex-start;
  }
  
  .quote-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .quote-time {
    margin-top: 0.25rem;
  }
}

/* Element UI overrides */
.el-dialog__body {
  padding-top: 10px;
}

.el-form-item {
  margin-bottom: 22px;
}
</style>